import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Box, Paper, Typography } from '@mui/material';
import { useClientDetails } from '@/hooks/useClientDetails';
import { LoadingState } from '@/components/clients/shared/LoadingState';
import { ErrorState } from '@/components/clients/shared/ErrorState';
import { NotFoundState } from '@/components/clients/shared/NotFoundState';
import { ClientQuestionnairesSection } from '@/clients/details/shared/components/ClientQuestionnairesSection';
import { ClientDetailsV2Breadcrumbs } from '@/clients/details/v2/components/ClientDetailsV2Breadcrumbs';
import { ClientDetailsV2RetentionBanner } from '@/clients/details/v2/components/ClientDetailsV2RetentionBanner';
import { ClientDetailsV2Header } from '@/clients/details/v2/components/ClientDetailsV2Header';
import { ClientDetailsV2OverviewTab } from '@/clients/details/v2/components/ClientDetailsV2OverviewTab';
import { ClientDetailsV2AppointmentsTab } from '@/clients/details/v2/components/ClientDetailsV2AppointmentsTab';
import { ClientDetailsV2SuggestedRdaTab } from '@/clients/details/v2/components/ClientDetailsV2SuggestedRdaTab';
import { ClientDetailsV2ClientTargetsTab } from '@/clients/details/v2/components/ClientDetailsV2ClientTargetsTab';
import { ClientDetailsV2AnalysisTab } from '@/clients/details/v2/components/ClientDetailsV2AnalysisTab';
import { ClientDetailsV2MealPlanTab } from '@/clients/details/v2/components/ClientDetailsV2MealPlanTab';
import { ClientDetailsV2InvoicesTab } from '@/clients/details/v2/components/ClientDetailsV2InvoicesTab';
import { ClientDetailsV2FlagsTab } from '@/clients/details/v2/components/ClientDetailsV2FlagsTab';
import { ClientLabReportsSection } from '@/clients/details/shared/components/ClientLabReportsSection';
import {
  buildClientDetailsV2ViewModel,
  getClientDetailsV2TabIndexFromRouteSegment,
  getClientDetailsV2TabRouteSegmentByIndex,
  panelTitleSx,
  tabPanelSx,
  tabs,
} from '@/clients/details/v2/components/clientDetailsV2.utils';
import { updateClient } from '@/lib/apiService';
import { mapMedicalConditionsToApiValues } from '@/utils/client-medical-conditions';
import { toFoodIntoleranceApiLabels } from '@/utils/food-intolerances';
import { showErrorToast, showSuccessToast } from '@/utils/toast-utils';
import ScheduleAppointmentDialog from '@/components/appointments/ScheduleAppointmentDialog';
import InviteQuestionnaireModal from '@/clients/onboarding/components/InviteQuestionnaireModal';
import { assignQuestionnaire } from '@/lib/api/questionnaires';
import { DeleteClientConfirmDialog } from '@/components/clients/DeleteClientConfirmDialog';
import { useDeleteClient } from '@/hooks/useDeleteClient';
import { isPendingDeletionClient } from '@/utils/client-account-deletion';

const analysisScopedQueryParams = ['mealPlanId', 'planId', 'day', 'dayNumber', 'genericAssigned'];

function buildClientDetailsTabQuery(searchParams: URLSearchParams, nextTabSegment: string): string {
  const nextParams = new URLSearchParams(searchParams);
  if (nextTabSegment !== 'analysis') {
    analysisScopedQueryParams.forEach((param) => nextParams.delete(param));
  }
  return nextParams.toString();
}

const ClientDetailsV2Page: React.FC = () => {
  const params = useParams();
  const clientId = params.id || '';
  const routeTabSegment = params['*']?.split('/')[0] || '';
  const routeTabIndex = React.useMemo(
    () => getClientDetailsV2TabIndexFromRouteSegment(routeTabSegment),
    [routeTabSegment],
  );
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = React.useState(routeTabIndex);
  const [shouldOpenAddNote, setShouldOpenAddNote] = React.useState(false);
  const [clientGoalsEditorAnchorEl, setClientGoalsEditorAnchorEl] = React.useState<HTMLElement | null>(null);

  const [isOverviewScheduleModalOpen, setIsOverviewScheduleModalOpen] = React.useState(false);
  const [overviewScheduleAppointmentId, setOverviewScheduleAppointmentId] = React.useState<string | null>(null);
  const [isOverviewQuestionnaireModalOpen, setIsOverviewQuestionnaireModalOpen] = React.useState(false);
  const [overviewSelectedQuestionnaireIds, setOverviewSelectedQuestionnaireIds] = React.useState<string[]>([]);
  const [isAssigningOverviewQuestionnaire, setIsAssigningOverviewQuestionnaire] = React.useState(false);
  const [overviewRefreshKey, setOverviewRefreshKey] = React.useState(0);

  const { client, activeSubscription, isLoading, error, refreshClientData, updateLocalClient } = useClientDetails(clientId);
  const {
    deleteTarget,
    isDeletingClient,
    isDeleteDialogOpen,
    requestDeleteClient,
    closeDeleteClient,
    confirmDeleteClient,
  } = useDeleteClient();

  const clientsQueryString = searchParams.toString();
  const viewModel = client ? buildClientDetailsV2ViewModel(client, clientsQueryString, activeSubscription) : null;

  React.useEffect(() => {
    setActiveTab(routeTabIndex);
  }, [routeTabIndex]);

  React.useEffect(() => {
    const resolvedTabSegment = getClientDetailsV2TabRouteSegmentByIndex(routeTabIndex);
    const normalizedRouteTabSegment = routeTabSegment.trim().toLowerCase();
    if (normalizedRouteTabSegment === resolvedTabSegment) return;

    const query = searchParams.toString();
    const nextPath = `/clients/${clientId}/details-v2/${resolvedTabSegment}${query ? `?${query}` : ''}`;
    navigate(nextPath, { replace: true });
  }, [clientId, navigate, routeTabIndex, routeTabSegment, searchParams]);

  const handleTabChange = React.useCallback(
    (nextTab: number) => {
      if (nextTab === activeTab) return;

      setActiveTab(nextTab);
      const nextTabSegment = getClientDetailsV2TabRouteSegmentByIndex(nextTab);
      const query = buildClientDetailsTabQuery(searchParams, nextTabSegment);
      const nextPath = `/clients/${clientId}/details-v2/${nextTabSegment}${query ? `?${query}` : ''}`;
      navigate(nextPath);
    },
    [activeTab, clientId, navigate, searchParams],
  );

  const handleOpenQuestionnaires = () => {
    handleTabChange(7);
  };

  const handleAddNote = () => {
    setShouldOpenAddNote(true);
    handleTabChange(9); // Notes tab
  };

  const handleRequestDeleteClient = React.useCallback(() => {
    if (!client) return;
    requestDeleteClient({
      id: clientId,
      name: viewModel?.clientDisplayName || client.fullName,
      status: client.status,
    });
  }, [client, clientId, requestDeleteClient, viewModel?.clientDisplayName]);

  const handleConfirmDeleteClient = React.useCallback(async () => {
    const deleted = await confirmDeleteClient();
    if (!deleted) return;
    navigate(viewModel?.clientsBackPath || '/clients', { replace: true });
  }, [confirmDeleteClient, navigate, viewModel?.clientsBackPath]);

  const handleOpenClientGoalsEditor = React.useCallback((anchorEl: HTMLElement) => {
    setClientGoalsEditorAnchorEl(anchorEl);
  }, []);

  const handleCloseClientGoalsEditor = React.useCallback(() => {
    setClientGoalsEditorAnchorEl(null);
  }, []);

  const handleUpdateAllergies = async (allergies: string[]) => {
    try {
      await updateClient(clientId, { allergies });
      showSuccessToast('Success', 'Allergies updated successfully');
      refreshClientData();
    } catch (err: unknown) {
      console.error('Failed to update allergies:', err);
      showErrorToast(err, 'Failed to update allergies');
    }
  };

  const handleUpdateIntolerances = async (intolerances: string[]) => {
    try {
      await updateClient(clientId, { intolerances: toFoodIntoleranceApiLabels(intolerances) });
      showSuccessToast('Success', 'Intolerances updated successfully');
      refreshClientData();
    } catch (err: unknown) {
      console.error('Failed to update intolerances:', err);
      showErrorToast(err, 'Failed to update intolerances');
    }
  };

  const handleUpdateMedicalConditions = async (
    medicalConditions: string[],
    medicalConditionSubtypes?: Record<string, string>,
  ) => {
    try {
      await updateClient(clientId, {
        medicalConditions: mapMedicalConditionsToApiValues(medicalConditions),
        medicalConditionSubtypes,
      });
      showSuccessToast('Success', 'Medical conditions updated successfully');
      refreshClientData();
      setOverviewRefreshKey((current) => current + 1);
    } catch (err: unknown) {
      console.error('Failed to update medical conditions:', err);
      showErrorToast(err, 'Failed to update medical conditions');
    }
  };

  const handleOpenOverviewAddAppointment = React.useCallback(() => {
    setOverviewScheduleAppointmentId(null);
    setIsOverviewScheduleModalOpen(true);
  }, []);

  const handleOpenOverviewReschedule = React.useCallback((appointmentId?: string) => {
    if (!appointmentId) return;
    setOverviewScheduleAppointmentId(appointmentId);
    setIsOverviewScheduleModalOpen(true);
  }, []);

  const handleCloseOverviewSchedule = React.useCallback(() => {
    setIsOverviewScheduleModalOpen(false);
    setOverviewScheduleAppointmentId(null);
  }, []);

  const handleOpenOverviewQuestionnaireModal = React.useCallback(() => {
    setIsOverviewQuestionnaireModalOpen(true);
  }, []);

  const handleCloseOverviewQuestionnaireModal = React.useCallback(() => {
    if (isAssigningOverviewQuestionnaire) return;
    setIsOverviewQuestionnaireModalOpen(false);
    setOverviewSelectedQuestionnaireIds([]);
  }, [isAssigningOverviewQuestionnaire]);

  const handleAssignOverviewQuestionnaires = React.useCallback(
    async (questionnaireIds: string[]) => {
      if (isAssigningOverviewQuestionnaire) return;

      const uniqueQuestionnaireIds = Array.from(new Set(questionnaireIds.filter(Boolean)));
      if (uniqueQuestionnaireIds.length === 0) return;

      setOverviewSelectedQuestionnaireIds(uniqueQuestionnaireIds);
      setIsAssigningOverviewQuestionnaire(true);

      try {
        await Promise.all(
          uniqueQuestionnaireIds.map((questionnaireId) =>
            assignQuestionnaire(questionnaireId, {
              clients: [{ clientId, sendEmail: true }],
            }),
          ),
        );

        const clientName = viewModel?.clientDisplayName || 'client';
        showSuccessToast(
          'Questionnaire assigned',
          uniqueQuestionnaireIds.length === 1
            ? `Assigned to ${clientName}.`
            : `${uniqueQuestionnaireIds.length} questionnaires assigned to ${clientName}.`,
        );

        setIsOverviewQuestionnaireModalOpen(false);
        setOverviewSelectedQuestionnaireIds([]);
        setOverviewRefreshKey((key) => key + 1);
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ['questionnaireResponses', clientId] }),
          queryClient.invalidateQueries({ queryKey: ['questionnaireSections'] }),
        ]);
      } catch (err) {
        showErrorToast(err, 'Failed to assign questionnaire');
      } finally {
        setIsAssigningOverviewQuestionnaire(false);
      }
    },
    [clientId, isAssigningOverviewQuestionnaire, queryClient, viewModel?.clientDisplayName],
  );

  if (isLoading && !client) return <LoadingState />;
  if (error && !client) return <ErrorState error={error} clientId={clientId} />;
  if (!client || !viewModel) return <NotFoundState />;

  return (
    <Box
      data-demo-id="client-details-shell"
      sx={{
        position: 'relative',
        py: { xs: 1, lg: 0 },
        mt: { xs: 1.5, lg: 0 },
        ml: { xs: 0.5, lg: 0 },
        overflowX: { xs: 'hidden', lg: 'auto' },
        overflowY: 'hidden',
      }}
    >
        <Paper
          elevation={0}
          sx={{
            width: { xs: '100%', lg: '1440px' },
            minWidth: { xs: 0, lg: '1440px' },
            minHeight: { xs: 'calc(100vh - 2rem)', lg: 'calc(100vh - 3.5rem)' },
            overflow: 'visible',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: { xs: '16px', lg: '20px' },
            border: '1px solid #dbe6eb',
            bgcolor: '#f8fbfd',
            opacity: 1,
            p: { xs: 2, sm: 2.5, lg: '24px' },
            gap: { xs: 2, lg: '24px' },
          }}
        >
          <ClientDetailsV2Breadcrumbs
            clientsBackPath={viewModel.clientsBackPath}
            clientDisplayName={viewModel.clientDisplayName}
          />

          <ClientDetailsV2RetentionBanner status={client.status} client={client} />

          <Box
            sx={{
              minWidth: 0,
              flex: 1,
              minHeight: 0,
              overflowY: 'visible',
              p: 0,
            }}
          >
            <ClientDetailsV2Header
              clientId={clientId}
              client={client}
              refreshClientData={refreshClientData}
              updateLocalClient={updateLocalClient}
              clientDisplayName={viewModel.clientDisplayName}
              emailDisplay={viewModel.emailDisplay}
              aboutDetails={viewModel.aboutDetails}
              allergies={viewModel.allergies}
              intolerances={viewModel.intolerances}
              conditionNames={viewModel.conditionNames}
              summaryItems={viewModel.summaryItems}
              metrics={viewModel.metrics}
              anthropometricItems={viewModel.anthropometricItems}
              activityAndGoalItems={viewModel.activityAndGoalItems}
              symptomItems={viewModel.symptomItems}
              dietaryPreferencesSummary={viewModel.dietaryPreferencesSummary}
              clientGoalSummary={viewModel.clientGoalSummary}
              nutrientGuidelinesSummary={viewModel.nutrientGuidelinesSummary}
              clientGoalRows={viewModel.clientGoalRows}
              completedQuestionnaires={viewModel.completedQuestionnaires}
              questionnaireCompletedLabel={viewModel.questionnaireCompletedLabel}
              nextAppointmentDate={viewModel.nextAppointmentDate}
              activeSubscriptionSummary={viewModel.activeSubscriptionSummary}
              statusLabel={viewModel.statusLabel}
              activeTab={activeTab}
              tabs={tabs}
              onTabChange={handleTabChange}
              onOpenQuestionnaires={handleOpenQuestionnaires}
              onUpdateAllergies={handleUpdateAllergies}
              onUpdateIntolerances={handleUpdateIntolerances}
              onUpdateMedicalConditions={handleUpdateMedicalConditions}
              onAddNote={handleAddNote}
              onDeleteClient={isPendingDeletionClient(client) ? undefined : handleRequestDeleteClient}
              clientGoalsEditorAnchorEl={clientGoalsEditorAnchorEl}
              onCloseClientGoalsEditor={handleCloseClientGoalsEditor}
            />

            {activeTab === 0 ? (
              <ClientDetailsV2OverviewTab
                client={client}
                clientDisplayName={viewModel.clientDisplayName}
                nextAppointmentDate={viewModel.nextAppointmentDate}
                goals={viewModel.goals}
                conditionNames={viewModel.conditionNames}
                biomarkerItems={viewModel.biomarkerItems}
                refreshKey={overviewRefreshKey}
                onAddAppointment={handleOpenOverviewAddAppointment}
                onOpenSuggestedRda={() => handleTabChange(2)}
                onOpenClientTargets={() => handleTabChange(3)}
                onOpenLabReports={() => handleTabChange(6)}
                onAssignQuestionnaire={handleOpenOverviewQuestionnaireModal}
                onOpenNotes={() => handleTabChange(9)}
                onRescheduleAppointment={handleOpenOverviewReschedule}
              />
            ) : activeTab === 1 ? (
              <ClientDetailsV2AppointmentsTab
                clientId={clientId}
                clientDisplayName={viewModel.clientDisplayName}
                clientEmail={viewModel.emailDisplay}
              />
            ) : activeTab === 2 ? (
              <ClientDetailsV2SuggestedRdaTab
                client={client}
                clientDisplayName={viewModel.clientDisplayName}
              />
            ) : activeTab === 3 ? (
              <ClientDetailsV2ClientTargetsTab
                client={client}
                onOpenClientGoalsEditor={handleOpenClientGoalsEditor}
              />
            ) : activeTab === 4 ? (
              <ClientDetailsV2AnalysisTab
                client={client}
                clientDisplayName={viewModel.clientDisplayName}
              />
            ) : activeTab === 5 ? (
              <ClientDetailsV2MealPlanTab clientId={clientId} client={client} />
            ) : activeTab === 6 ? (
              <Paper elevation={0} sx={{ ...tabPanelSx, bgcolor: 'transparent', border: 'none', p: 0 }}>
                <ClientLabReportsSection
                  clientId={clientId}
                  clientName={viewModel.clientDisplayName}
                  onRefresh={refreshClientData}
                />
              </Paper>
            ) : activeTab === 7 ? (
              <Paper elevation={0} sx={tabPanelSx}>
                <ClientQuestionnairesSection clientId={clientId} />
              </Paper>
            ) : activeTab === 8 ? (
              <Paper elevation={0} sx={{ ...tabPanelSx, bgcolor: 'transparent', border: 'none', p: 0 }}>
                <ClientDetailsV2InvoicesTab clientId={clientId} />
              </Paper>
            ) : activeTab === 9 ? (
              <Paper elevation={0} sx={{ ...tabPanelSx, bgcolor: 'transparent', border: 'none', p: 0 }}>
                <ClientDetailsV2FlagsTab
                  clientId={clientId}
                  shouldOpenAddNote={shouldOpenAddNote}
                  onAddNoteHandled={() => setShouldOpenAddNote(false)}
                />
              </Paper>
            ) : (
              <Paper elevation={0} sx={tabPanelSx}>
                <Typography sx={panelTitleSx}>{tabs[activeTab]}</Typography>
                <Typography sx={{ mt: 1, fontFamily: 'Inter, sans-serif', color: '#667085', overflowWrap: 'anywhere' }}>
                  This section is available in the new responsive shell. Existing legacy view is still preserved during migration.
                </Typography>
              </Paper>
            )}
          </Box>
        </Paper>

        <ScheduleAppointmentDialog
          open={isOverviewScheduleModalOpen}
          onClose={handleCloseOverviewSchedule}
          appointmentIdToEdit={overviewScheduleAppointmentId}
          defaultClientId={clientId}
          lockedClient={{
            id: clientId,
            name: viewModel.clientDisplayName,
            email: viewModel.emailDisplay,
          }}
          onAppointmentCreated={() => {
            refreshClientData();
            setOverviewRefreshKey((key) => key + 1);
          }}
          onAppointmentUpdated={() => {
            refreshClientData();
            setOverviewRefreshKey((key) => key + 1);
          }}
        />

        <InviteQuestionnaireModal
          open={isOverviewQuestionnaireModalOpen}
          onClose={handleCloseOverviewQuestionnaireModal}
          clientFirstName={client.firstName || viewModel.clientDisplayName}
          clientLastName={client.lastName}
          clientEmail={client.email || viewModel.emailDisplay}
          selectedQuestionnaireIds={overviewSelectedQuestionnaireIds}
          onAttach={handleAssignOverviewQuestionnaires}
          titleText="Assign Questionnaire"
          helperText="Select questionnaire(s) to assign to this client."
          attachButtonText={isAssigningOverviewQuestionnaire ? 'Assigning...' : 'Assign Questionnaire'}
        />

        <DeleteClientConfirmDialog
          open={isDeleteDialogOpen}
          clientName={deleteTarget?.name || viewModel.clientDisplayName}
          isPendingDeletion={deleteTarget?.isPendingDeletion ?? isPendingDeletionClient(client)}
          isDeleting={isDeletingClient}
          onOpenChange={(open) => {
            if (!open) closeDeleteClient();
          }}
          onConfirm={() => {
            void handleConfirmDeleteClient();
          }}
        />
    </Box>
  );
};

export default ClientDetailsV2Page;
